// Shared data layer for the SEO Audit feature: API root resolution and one
// async function per backend endpoint under /ai-suite/audit (modeled on reportApi.js).
import helpers from "../helper/global"
import CryptoJS from "crypto-js"
import { getMonitoringBaseUrl } from "../../utils/aiSuiteProjectSettings"

export function resolveApiRoot(companyId) {
    return getMonitoringBaseUrl({ companyIdEncrypted: companyId, cryptoJS: CryptoJS })
}

// explicit timeout: a hung request must not block the audit views forever
const DEFAULT_TIMEOUT = 90000
// on-demand fix generation re-fetches the page and runs one LLM call — that needs longer
const FIXES_TIMEOUT = 300000

function post(companyId, path, payload, timeout = DEFAULT_TIMEOUT) {
    return helpers.axiosPost(resolveApiRoot(companyId) + path, { company_id: companyId, ...payload }, timeout)
}

// Start a new audit run. Returns the raw response body:
// { status: "started", run_id } on success or, when the credit limit is hit (HTTP 200):
// { error: "not_enough_credits", status: "not enough credits", credits: <limit>, count: <used>, credits_needed: <n> }
export async function createRun(companyId, { urls, name, projectId, focus, gapFor }) {
    const payload = { urls }
    if (name) payload.name = name
    if (projectId) payload.project_id = projectId
    // optional focus keywords/prompts the audited pages should be found for — omitted when empty
    if (Array.isArray(focus) && focus.length) payload.focus = focus
    // optional: this run audits the competitor page cited instead of an existing result
    if (gapFor && gapFor.runId && gapFor.resultId) {
        payload.gap_for = { run_id: gapFor.runId, result_id: gapFor.resultId, prompts: Array.isArray(gapFor.prompts) ? gapFor.prompts : [] }
    }
    const res = await post(companyId, "/ai-suite/audit/runs/create", payload)
    return res?.data || {}
}

export async function fetchRuns(companyId, projectId = null) {
    const payload = {}
    if (projectId) payload.project_id = projectId
    const res = await post(companyId, "/ai-suite/audit/runs/get", payload)
    return Array.isArray(res?.data?.runs) ? res.data.runs : []
}

// One run incl. per-URL summary rows: { run, results_summary }
export async function fetchRun(companyId, runId) {
    const res = await post(companyId, "/ai-suite/audit/runs/one/get", { run_id: runId })
    return res?.data || {}
}

// Full result docs of a run; pass resultId to get a single one.
export async function fetchResults(companyId, runId, resultId = null) {
    const payload = { run_id: runId }
    if (resultId) payload.result_id = resultId
    const res = await post(companyId, "/ai-suite/audit/results/get", payload)
    return Array.isArray(res?.data?.results) ? res.data.results : []
}

export async function deleteRun(companyId, runId) {
    const res = await post(companyId, "/ai-suite/audit/runs/delete", { run_id: runId })
    return res?.data || {}
}

// Most-cited URLs of an AI project (from its tracked sources).
export async function suggestSources(companyId, { projectId, domain, limit }) {
    const payload = { project_id: projectId }
    if (domain) payload.domain = domain
    if (limit) payload.limit = limit
    const res = await post(companyId, "/ai-suite/audit/sources/suggest", payload)
    return Array.isArray(res?.data?.sources) ? res.data.sources : []
}

// Tracked prompts of an AI project for the focus picker.
// Returns { prompts: [String], total: Number } — total is the tracked total, which can be
// larger than the returned list (server-side limit / search filter).
export async function suggestPrompts(companyId, { projectId, search, limit }) {
    const payload = { project_id: projectId }
    if (search) payload.search = search
    if (limit) payload.limit = limit
    const res = await post(companyId, "/ai-suite/audit/prompts/suggest", payload)
    const prompts = Array.isArray(res?.data?.prompts) ? res.data.prompts.filter((p) => typeof p === "string" && p.trim().length) : []
    const total = Number(res?.data?.total)
    return { prompts, total: isFinite(total) ? total : prompts.length }
}

// Copy-paste-ready fixes for one result, generated on demand (never during the audit run).
// Returns { fixes, cached } — cached: true means the stored fixes came back without an LLM call.
// Pass regenerate: true to force a fresh generation.
export async function generateFixes(companyId, { runId, resultId, regenerate = false }) {
    const payload = { run_id: runId, result_id: resultId }
    if (regenerate) payload.regenerate = true
    const res = await post(companyId, "/ai-suite/audit/fixes/generate", payload, FIXES_TIMEOUT)
    return res?.data || {}
}

// Audit credits: { audits: <used>, audits_limit: <limit> }
export async function fetchCredits(companyId) {
    const res = await post(companyId, "/ai-suite/audit/credits/get", {})
    return res?.data || {}
}

// ---- prompt gap: who gets cited instead of this page? ----

// Pages cited in AI answers for the given tracked prompts (own domain excluded server-side).
// -> { cited: [{ url, domain, citations, prompts, prompt_count, platforms, last_seen }], prompts_checked }
export async function fetchCitedPages(companyId, { projectId, prompts, excludeUrl, limit }) {
    const payload = { project_id: projectId, prompts }
    if (excludeUrl) payload.exclude_url = excludeUrl
    if (limit) payload.limit = limit
    const res = await post(companyId, "/ai-suite/audit/gap/cited", payload)
    return res?.data || { cited: [], prompts_checked: 0 }
}

// Competitor audits that were started for a result
// -> { gap_runs: [{ run_id, status, error, url, prompts, competitor_result_id, createdAt }] }
export async function fetchGapRuns(companyId, { runId, resultId }) {
    const res = await post(companyId, "/ai-suite/audit/gap/list", { run_id: runId, result_id: resultId })
    return Array.isArray(res?.data?.gap_runs) ? res.data.gap_runs : []
}

// Side-by-side comparison of the own result with a finished competitor result
// -> { comparison, mine, theirs }
export async function compareGap(companyId, { runId, resultId, competitorRunId, competitorResultId }) {
    const res = await post(companyId, "/ai-suite/audit/gap/compare", {
        run_id: runId,
        result_id: resultId,
        competitor_run_id: competitorRunId,
        competitor_result_id: competitorResultId
    })
    return res?.data || null
}

// ---- impact: audit history of a URL against its citations ----
// -> { impact: { window_days, audits, weekly, total_citations }, has_project, days }
// Re-check the coverage of one result after its prompt selection changed — only the coverage
// call runs (no new audit, no credit). mode "manual" needs prompts[] (max 20), mode "auto"
// recomputes the automatic selection. Returns { coverage }.
export async function recheckCoverage(companyId, { runId, resultId, mode, prompts }) {
    const payload = { run_id: runId, result_id: resultId, mode }
    if (Array.isArray(prompts) && prompts.length) payload.prompts = prompts
    const res = await post(companyId, "/ai-suite/audit/coverage/recheck", payload, FIXES_TIMEOUT)
    return res?.data || {}
}

export async function fetchImpact(companyId, { runId, resultId, days }) {
    const payload = { run_id: runId, result_id: resultId }
    if (days) payload.days = days
    const res = await post(companyId, "/ai-suite/audit/impact/get", payload)
    return res?.data || null
}
